import React, { useState, useMemo, useRef, useEffect, useContext, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { UserContext, WorkspaceContext } from '../../../objects/Context';
import './Transactions.css';

const PAGE_SIZE = 20;
const SKELETON_ROWS = 8;
const COL_COUNT = 8;

const FLOW_MODES = ['All', 'Inflows', 'Outflows'];

const SOURCE_TXN_TYPES = [
  'Invoice', 'Sales Receipt', 'Bill', 'Credit Card Expense',
  'Credit Card Credit', 'Cash Expense', 'Payment', 'Bill Payment',
  'Deposit', 'Credit Memo', 'Refund', 'Vendor Credit',
  'Journal Entry', 'Transfer',
];

const FILTER_CATEGORIES = [
  { key: 'bankAccount',   label: 'Bank Account' },
  { key: 'sourceAccount', label: 'Source Account' },
  { key: 'sourceTxn',     label: 'Source Txn' },
  { key: 'amount',        label: 'Amount' },
];

const DATE_OPTIONS = [
  { key: 'past_month',    label: 'Past Month' },
  { key: 'past_3_months', label: 'Past 3 Months' },
  { key: 'past_6_months', label: 'Past 6 Months' },
  { key: 'past_year',     label: 'Past Year' },
  { key: 'all',           label: 'All Time' },
];

const fmtDate = (dateStr) => {
  if (!dateStr) return '—';
  const raw = typeof dateStr === 'string' ? dateStr.split('T')[0] : dateStr;
  const d = new Date(raw + 'T00:00:00Z');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
};

const fmtCurrency = (v) => {
  if (v == null) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(v);
};

const TYPE_COLORS = {
  'Invoice':              { bg: 'rgba(72, 143, 243, 0.10)',  color: '#2e6ecf' },
  'Bill Payment':         { bg: 'rgba(220, 60, 60, 0.10)',   color: '#c0392b' },
  'Bill':                 { bg: 'rgba(220, 60, 60, 0.10)',   color: '#c0392b' },
  'Journal Entry':        { bg: 'rgba(120, 90, 200, 0.10)',  color: '#6c44b8' },
  'Deposit':              { bg: 'rgba(17, 140, 79, 0.10)',   color: '#118c4f' },
  'Payment':              { bg: 'rgba(17, 140, 79, 0.10)',   color: '#118c4f' },
  'Sales Receipt':        { bg: 'rgba(17, 140, 79, 0.10)',   color: '#118c4f' },
  'Cash Expense':         { bg: 'rgba(220, 60, 60, 0.10)',   color: '#c0392b' },
  'Credit Card Expense':  { bg: 'rgba(220, 100, 40, 0.10)',  color: '#b35415' },
  'Credit Card Credit':   { bg: 'rgba(72, 143, 243, 0.10)',  color: '#2e6ecf' },
  'Credit Memo':          { bg: 'rgba(72, 143, 243, 0.10)',  color: '#2e6ecf' },
  'Refund':               { bg: 'rgba(220, 60, 60, 0.10)',   color: '#c0392b' },
  'Vendor Credit':        { bg: 'rgba(120, 90, 200, 0.10)',  color: '#6c44b8' },
  'Transfer':             { bg: 'rgba(0,0,0,0.06)',          color: '#555' },
};

const DEFAULT_FLOW = 'All';
const DEFAULT_DATE = 'all';

function computeDateFrom(dateKey) {
  if (dateKey === 'all') return null;
  const now = new Date();
  let cutoff;
  if (dateKey === 'past_month')    cutoff = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
  if (dateKey === 'past_3_months') cutoff = new Date(now.getFullYear(), now.getMonth() - 3, now.getDate());
  if (dateKey === 'past_6_months') cutoff = new Date(now.getFullYear(), now.getMonth() - 6, now.getDate());
  if (dateKey === 'past_year')     cutoff = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
  if (!cutoff) return null;
  return cutoff.toISOString().split('T')[0];
}

// ── Skeleton row ────────────────────────────────────────────────────────────
const SkeletonRow = ({ idx }) => (
  <tr className="txn-row txn-skeleton-row">
    {Array.from({ length: COL_COUNT }).map((_, c) => (
      <td key={c}>
        <div
          className="skeleton-bar"
          style={{ width: c === 0 ? '70%' : c === COL_COUNT - 1 ? '60%' : `${55 + ((idx + c) % 4) * 12}%` }}
        />
      </td>
    ))}
  </tr>
);

const Transactions = () => {
  const { user } = useContext(UserContext);
  const { workspace } = useContext(WorkspaceContext);
  const [searchParams, setSearchParams] = useSearchParams();

  // Hydrate initial state from URL search params (enables pre-population)
  const initialBankAccounts   = useMemo(() => searchParams.get('bankAccounts')?.split(',').filter(Boolean) || [], []);
  const initialSourceAccounts = useMemo(() => searchParams.get('sourceAccounts')?.split(',').filter(Boolean) || [], []);
  const initialSourceTxn      = useMemo(() => searchParams.get('sourceTxnTypes')?.split(',').filter(Boolean) || [], []);
  const initialMinAmount      = useMemo(() => searchParams.get('minAmount') || '', []);
  const initialFlow           = useMemo(() => searchParams.get('flow') || DEFAULT_FLOW, []);
  const initialDate           = useMemo(() => searchParams.get('date') || DEFAULT_DATE, []);

  const [flowMode, setFlowMode]     = useState(initialFlow);
  const [dateRange, setDateRange]    = useState(initialDate);
  const [bankAccountFilter, setBankAccountFilter]     = useState(initialBankAccounts);
  const [sourceAccountFilter, setSourceAccountFilter] = useState(initialSourceAccounts);
  const [sourceTxnFilter, setSourceTxnFilter]         = useState(initialSourceTxn);
  const [minAmountInput, setMinAmountInput]            = useState(initialMinAmount);
  const [minAmountApplied, setMinAmountApplied]        = useState(initialMinAmount);

  // Data
  const [rows, setRows]       = useState([]);
  const [total, setTotal]     = useState(0);
  const [page, setPage]       = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  // Dropdown options (fetched from backend)
  const [bankOptions, setBankOptions]     = useState([]);
  const [sourceOptions, setSourceOptions] = useState([]);
  const [optionsLoaded, setOptionsLoaded] = useState(false);

  // Dropdown UI state
  const [filterOpen, setFilterOpen]     = useState(false);
  const [filterDrill, setFilterDrill]   = useState(null);
  const filterRef = useRef(null);
  const [dateOpen, setDateOpen] = useState(false);
  const dateRef = useRef(null);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (filterRef.current && !filterRef.current.contains(e.target)) {
        setFilterOpen(false);
        setFilterDrill(null);
      }
      if (dateRef.current && !dateRef.current.contains(e.target)) setDateOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Fetch dropdown options once
  useEffect(() => {
    if (!user?.id || !workspace?.id || optionsLoaded) return;
    const base = `${process.env.REACT_APP_API_BASE_URL}/api/queries/top-accounts/${user.id}/${workspace.id}`;
    Promise.all([
      axios.get(`${base}?kind=bank&topN=15`, { withCredentials: true }),
      axios.get(`${base}?kind=source&topN=15`, { withCredentials: true }),
    ]).then(([bankRes, srcRes]) => {
      setBankOptions(bankRes.data?.accounts || []);
      setSourceOptions(srcRes.data?.accounts || []);
      setOptionsLoaded(true);
    }).catch(() => setOptionsLoaded(true));
  }, [user?.id, workspace?.id, optionsLoaded]);

  // Fetch transactions
  const fetchTransactions = useCallback(async (pageNum) => {
    if (!user?.id || !workspace?.id) return;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.set('limit', PAGE_SIZE);
      params.set('offset', pageNum * PAGE_SIZE);
      if (flowMode !== 'All') params.set('flowMode', flowMode);
      if (bankAccountFilter.length > 0)   params.set('bankAccounts',   bankAccountFilter.join(','));
      if (sourceAccountFilter.length > 0) params.set('sourceAccounts', sourceAccountFilter.join(','));
      if (sourceTxnFilter.length > 0)     params.set('sourceTxnTypes', sourceTxnFilter.join(','));
      if (minAmountApplied)               params.set('minAmount',      minAmountApplied);
      const dateFrom = computeDateFrom(dateRange);
      if (dateFrom) params.set('dateFrom', dateFrom);

      const url = `${process.env.REACT_APP_API_BASE_URL}/api/queries/transactions/${user.id}/${workspace.id}?${params.toString()}`;
      const res = await axios.get(url, { withCredentials: true });
      if (res.data?.success) {
        setRows(res.data.rows || []);
        setTotal(res.data.total || 0);
      } else {
        setRows([]); setTotal(0);
      }
    } catch (err) {
      console.error('Failed to fetch transactions:', err);
      setError('Failed to load transactions.');
      setRows([]); setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [user?.id, workspace?.id, flowMode, bankAccountFilter, sourceAccountFilter, sourceTxnFilter, minAmountApplied, dateRange]);

  useEffect(() => { fetchTransactions(page); }, [page, fetchTransactions]);
  useEffect(() => { setPage(0); }, [flowMode, bankAccountFilter, sourceAccountFilter, sourceTxnFilter, minAmountApplied, dateRange]);

  // ── Filter helpers ──
  const toggleList = (setter, value) => {
    setter(prev => prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]);
  };

  const removeChip = (category, value) => {
    if (category === 'bankAccount')   setBankAccountFilter(p => p.filter(v => v !== value));
    if (category === 'sourceAccount') setSourceAccountFilter(p => p.filter(v => v !== value));
    if (category === 'sourceTxn')     setSourceTxnFilter(p => p.filter(v => v !== value));
    if (category === 'amount')        { setMinAmountInput(''); setMinAmountApplied(''); }
  };

  const resetAll = () => {
    const anyActive =
      flowMode !== DEFAULT_FLOW ||
      dateRange !== DEFAULT_DATE ||
      (bankAccountFilter && bankAccountFilter.length > 0) ||
      (sourceAccountFilter && sourceAccountFilter.length > 0) ||
      (sourceTxnFilter && sourceTxnFilter.length > 0) ||
      (minAmountApplied && String(minAmountApplied).trim() !== '');

    if (!anyActive) return;

    setFlowMode(DEFAULT_FLOW);
    setBankAccountFilter([]);
    setSourceAccountFilter([]);
    setSourceTxnFilter([]);
    setMinAmountInput('');
    setMinAmountApplied('');
    setDateRange(DEFAULT_DATE);
    setFilterOpen(false);
    setFilterDrill(null);
    setDateOpen(false);
    setSearchParams({});
  };

  // Build chips
  const activeChips = useMemo(() => {
    const chips = [];
    bankAccountFilter.forEach(v   => chips.push({ category: 'bankAccount',   label: `Bank: ${v}`, value: v }));
    sourceAccountFilter.forEach(v => chips.push({ category: 'sourceAccount', label: `Source: ${v}`, value: v }));
    sourceTxnFilter.forEach(v     => chips.push({ category: 'sourceTxn',     label: `Txn: ${v}`, value: v }));
    if (minAmountApplied) chips.push({ category: 'amount', label: `Min $${minAmountApplied}`, value: minAmountApplied });
    return chips;
  }, [bankAccountFilter, sourceAccountFilter, sourceTxnFilter, minAmountApplied]);

  const totalPages = Math.ceil(total / PAGE_SIZE);
  const selectedDateLabel = DATE_OPTIONS.find(d => d.key === dateRange)?.label || 'Date';

  // ── Drill-down content renderers ──
  const renderCheckboxList = (options, selected, onToggle, emptyMsg) => {
    if (options.length === 0) return <div className="dropdown-empty">{emptyMsg || 'No options'}</div>;
    return options.map(opt => {
      const isChecked = selected.includes(opt);
      return (
        <button key={opt} className={`dropdown-option selectable ${isChecked ? 'selected' : ''}`} onClick={() => onToggle(opt)}>
          <span className={`check-box ${isChecked ? 'checked' : ''}`}>{isChecked && '✓'}</span>
          <span>{opt}</span>
        </button>
      );
    });
  };

  const renderAmountInput = () => (
    <div className="dropdown-amount-section">
      <label className="dropdown-amount-label">Minimum absolute amount</label>
      <div className="dropdown-amount-row">
        <span className="dropdown-amount-prefix">$</span>
        <input
          className="dropdown-amount-input"
          type="number"
          min="0"
          step="any"
          placeholder="e.g. 500"
          value={minAmountInput}
          onChange={e => setMinAmountInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { setMinAmountApplied(minAmountInput); setFilterOpen(false); setFilterDrill(null); } }}
        />
        <button
          className="dropdown-amount-apply"
          onClick={() => { setMinAmountApplied(minAmountInput); setFilterOpen(false); setFilterDrill(null); }}
        >
          Apply
        </button>
      </div>
    </div>
  );

  const drillBackLabel = FILTER_CATEGORIES.find(c => c.key === filterDrill)?.label || '';

  return (
    <div className="wrapper txn-wrapper">
      <div className="txn-page">

        {/* ── Header ── */}
        <div className="txn-header">
          <div className="txn-title-group">
            <h1 className="txn-title">Transactions</h1>
            <h2 className="txn-subtitle">Transaction-level financial data</h2>
          </div>

          <div className="txn-header-controls">
            <div className="txn-toggle-strip">
              {FLOW_MODES.map(mode => (
                <button key={mode} className={`toggle-btn ${flowMode === mode ? 'active' : ''}`} onClick={() => setFlowMode(mode)}>
                  {mode}
                </button>
              ))}
            </div>

            {/* Filter dropdown */}
            <div className="txn-filter-dropdown-anchor" ref={filterRef}>
              <button
                className={`txn-control-btn ${activeChips.length > 0 ? 'has-filters' : ''}`}
                onClick={() => { setFilterOpen(p => !p); setFilterDrill(null); }}
              >
                Filter{activeChips.length > 0 && <span className="filter-count">{activeChips.length}</span>}
              </button>

              {filterOpen && (
                <div className="txn-dropdown filter-dropdown">
                  {filterDrill === null ? (
                    <>
                      <div className="dropdown-heading">Filter by</div>
                      {FILTER_CATEGORIES.map(cat => (
                        <button key={cat.key} className="dropdown-option" onClick={() => setFilterDrill(cat.key)}>
                          <span>{cat.label}</span>
                          <span className="dropdown-arrow">›</span>
                        </button>
                      ))}
                    </>
                  ) : (
                    <>
                      <button className="dropdown-back" onClick={() => setFilterDrill(null)}>
                        <span style={{ marginRight: 8 }}>‹</span>
                        <span>{drillBackLabel}</span>
                      </button>
                      {filterDrill === 'bankAccount'   && renderCheckboxList(bankOptions, bankAccountFilter, v => toggleList(setBankAccountFilter, v), 'Loading...')}
                      {filterDrill === 'sourceAccount' && renderCheckboxList(sourceOptions, sourceAccountFilter, v => toggleList(setSourceAccountFilter, v), 'Loading...')}
                      {filterDrill === 'sourceTxn'     && renderCheckboxList(SOURCE_TXN_TYPES, sourceTxnFilter, v => toggleList(setSourceTxnFilter, v))}
                      {filterDrill === 'amount'        && renderAmountInput()}
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Date selector */}
            <div className="txn-filter-dropdown-anchor" ref={dateRef}>
              <button className={`txn-control-btn ${dateRange !== DEFAULT_DATE ? 'has-filters' : ''}`} onClick={() => setDateOpen(p => !p)}>
                {selectedDateLabel}
              </button>
              {dateOpen && (
                <div className="txn-dropdown date-dropdown">
                  {DATE_OPTIONS.map(opt => (
                    <button key={opt.key} className={`dropdown-option ${dateRange === opt.key ? 'selected' : ''}`} onClick={() => { setDateRange(opt.key); setDateOpen(false); }}>
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <button className="txn-reset-btn" onClick={resetAll}>Reset</button>
          </div>
        </div>

        {/* ── Active filter chips ── */}
        {activeChips.length > 0 && (
          <div className="txn-active-filters">
            {activeChips.map(chip => (
              <span key={`${chip.category}-${chip.value}`} className="filter-chip">
                <span className="chip-label">{chip.label}</span>
                <button className="chip-remove" onClick={() => removeChip(chip.category, chip.value)}>×</button>
              </span>
            ))}
          </div>
        )}

        {/* ── Table ── */}
        <div className="txn-table-section">
          <div className="txn-table-wrapper">
            <table className="txn-table">
              <thead>
                <tr>
                  <th className="col-txn-id">TXN ID</th>
                  <th className="col-type">TYPE</th>
                  <th className="col-date">DATE</th>
                  <th className="col-bank">BANK ACCOUNT</th>
                  <th className="col-source-txn">SOURCE TXN</th>
                  <th className="col-source-cat">SOURCE CATEGORY</th>
                  <th className="col-source-acct">SOURCE ACCOUNT</th>
                  <th className="col-amount">AMOUNT</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  Array.from({ length: SKELETON_ROWS }).map((_, i) => <SkeletonRow key={i} idx={i} />)
                ) : error ? (
                  <tr><td colSpan={COL_COUNT} className="txn-empty-row txn-error-row">{error}</td></tr>
                ) : rows.length === 0 ? (
                  <tr><td colSpan={COL_COUNT} className="txn-empty-row">No transactions match the current filters.</td></tr>
                ) : (
                  rows.map((txn, idx) => {
                    const typeStyle = TYPE_COLORS[txn.type] || { bg: 'rgba(0,0,0,0.06)', color: '#555' };
                    const srcStyle  = TYPE_COLORS[txn.source_txn_type] || { bg: 'rgba(0,0,0,0.06)', color: '#555' };
                    return (
                      <tr key={`${txn.txn_id}-${idx}`} className="txn-row">
                        <td className="col-txn-id txn-id-cell">{txn.txn_id}</td>
                        <td className="col-type">
                          <span className="type-badge" style={{ backgroundColor: typeStyle.bg, color: typeStyle.color }}>{txn.type}</span>
                        </td>
                        <td className="col-date">{fmtDate(txn.date)}</td>
                        <td className="col-bank">{txn.bank_account || '—'}</td>
                        <td className="col-source-txn">
                          {txn.source_txn_type
                            ? <span className="type-badge" style={{ backgroundColor: srcStyle.bg, color: srcStyle.color }}>{txn.source_txn_type}</span>
                            : '—'}
                        </td>
                        <td className="col-source-cat">{txn.source_category || '—'}</td>
                        <td className="col-source-acct">{txn.source_account || '—'}</td>
                        <td className={`col-amount ${txn.amount >= 0 ? 'positive' : 'negative'}`}>{fmtCurrency(txn.amount)}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="txn-pagination">
              <button className="txn-page-btn" disabled={page === 0} onClick={() => setPage(p => Math.max(0, p - 1))}>‹ Prev</button>
              <span className="txn-page-info">Page {page + 1} of {totalPages}</span>
              <button className="txn-page-btn" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>Next ›</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Transactions;
